import React, { useState, useEffect, useMemo, useContext } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, TextField } from "@mui/material";
import { ColorPicker } from 'material-ui-color';
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import config from 'config'
import { Palette } from "color-thief-react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import ImageHandler from "components/ImageHandler";

const defaultURI = `${config.apiGateway.URL}/${config.apiGateway.DEFAULT_VERSION}`;

export default function Appearance () {
    const { updateTheme, error } = useContext(PrivateRoutesContext);

    const sqrImgMaxWidth = 500;
    const sqrImgMaxHeight = 500;

    const rectImgMaxWidth = 1000;
    const rectImgMaxHeight = 500;

    const [tenantName, setTenantName] = useState("");
    const [sqrLogo, setSqrLogo] = useState(null);
    const [sqrLogoIsUpdated, setSqrLogoIsUpdated] = useState(false);
    const [rectLogo, setRectLogo] = useState(null);
    const [rectLogoIsUpdated, setRectLogoIsUpdated] = useState(false);
    const [primary, setPrimary] = useState("");
    const [primaryText, setPrimaryText] = useState("");
    const [secondary, setSecondary] = useState("");
    const [isValid, setIsValid] = useState(true);

    const [copiedColor, setCopiedColor] = useState("");

    const sqrLogoSrc = useMemo(
        () => sqrLogo ? ((typeof sqrLogo === 'string' || sqrLogo instanceof String) ? sqrLogo : window.URL.createObjectURL(sqrLogo)) : null,
        [sqrLogo]
      )
      
    const rectLogoSrc = useMemo(
        () => rectLogo ? ((typeof rectLogo === 'string' || rectLogo instanceof String) ? rectLogo : window.URL.createObjectURL(rectLogo)) : null,
        [rectLogo]
      )

    useEffect(() => {
        apiGet(apiUrls.getTheme).then((response) => {
            setTenantName(response.data.tenantName);
            setPrimary(response.data.primary);
            setPrimaryText(response.data.primaryText);
            setSecondary(response.data.secondary);
            setSqrLogo(`${defaultURI}/Me/TenantLogo/sqr`);
            setRectLogo(`${defaultURI}/Me/TenantLogo/rect`);
        });
    },[]);

    const handleUpdateAppearance = () => {
        const formData = new FormData();
        formData.append("tenantName", tenantName);
        formData.append("primary", getColor(primary));
        formData.append("primaryText", getColor(primaryText));
        formData.append("secondary", getColor(secondary));
        formData.append("squareLogo", sqrLogo);
        formData.append("isSquareLogoUpdated", sqrLogoIsUpdated);
        formData.append("rectangularLogo", rectLogo);
        formData.append("isRectangularLogoUpdated", rectLogoIsUpdated);

        apiPost(apiUrls.setTheme, formData, {
            headers: {
              "content-type": "multipart/form-data",
              Accept: "*/*",
            }}).then((response) => {
                if (updateTheme) {
                    updateTheme();
                }
            });
    }

    const handleResetToDefault = () => {
        setPrimary("#f16824");
        setPrimaryText("#000");
        setSecondary("#03049b");
    }

    const getColor = (value) => {
        if (value) {
            if (typeof value === 'string' || value instanceof String) {
                return value;
            }
            else {
                return `#${value.hex}`;
            }
        }
    }

    const setColor = (value,setter) => {
        if (value) {
            setter(value);
            setIsValid(value.error === undefined)
        }
    }

    const onlyUnique = (value, index, self) => {
        return self.indexOf(value) === index;
    }

    const copyColor = (color) => {
        setCopiedColor(color);
        navigator.clipboard.writeText(color)
    }

    return (
        <Box className="container-fluid">
            <Box component="h2">Adjust Theme</Box>
            <Box className="mb-2">
                <TextField
                    id="tenant-name"
                    label="Broker Name"
                    variant="standard"
                    value={tenantName}
                    onChange={e => setTenantName(e.target.value)}
                />
            </Box>
            <Box className="mb-2 row">
                <ImageHandler image={{
                    title: "Rectangular Logo",
                    setLogo: setRectLogo,
                    setIsUpdated: setRectLogoIsUpdated,
                    maxWidth: rectImgMaxWidth,
                    maxHeight: rectImgMaxHeight,
                    src: rectLogoSrc,
                }} />
                <ImageHandler image={{
                    title: "Square Logo",
                    setLogo: setSqrLogo,
                    setIsUpdated: setSqrLogoIsUpdated,
                    maxWidth: sqrImgMaxWidth,
                    maxHeight: sqrImgMaxHeight,
                    src: sqrLogoSrc,
                }} />
            </Box>
            {rectLogoSrc ?
                <Box className="mb-2 row">
                    <Box className="col-lg-3">
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                            >
                                View Palette
                            </AccordionSummary>
                            <AccordionDetails>
                                <Palette src={rectLogoSrc} crossOrigin="anonymous" format="hex" colorCount={8} quality={10}>
                                    {({ data, loading }) => {
                                        if (loading) return <div>Loading...</div>;
                                        if (data === undefined) return <div>Unable to load.</div>
                                        return (
                                            <div>
                                            Palette:
                                            <ul>
                                                {data.filter(onlyUnique).map((color, index) => (
                                                <Box
                                                    component="li"
                                                    key={index}
                                                    sx={{ color: color, cursor: "pointer"}}
                                                    onClick={() => copyColor(color)}
                                                >
                                                    <strong>{`${color}${copiedColor === color ? " - Copied to clipboard" : ""}`}</strong>
                                                </Box>
                                                ))}
                                            </ul>
                                            </div>
                                        );
                                    }}
                                </Palette>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            : null}
            <Box className="d-flex align-items-center">
                <Box className="px-1">
                    <Box>Primary</Box>
                    <ColorPicker value={primary} disablePlainColor onChange={e => setColor(e,setPrimary)}/>
                </Box>
                <Box className="px-1">
                    <Box>Primary Text</Box>
                    <ColorPicker value={primaryText} disablePlainColor  onChange={e => setColor(e,setPrimaryText)}/>
                </Box>
                <Box className="px-1">
                    <Box>Secondary</Box>
                    <ColorPicker value={secondary} disablePlainColor  onChange={e => setColor(e,setSecondary)}/>
                </Box>
                <Box className="px-1">
                    <Button variant="outlined" onClick={handleResetToDefault}>Reset to Default</Button>
                </Box>
            </Box>
            <Box className="d-flex">
                <Box className="px-1">
                    <Button variant="outlined" onClick={handleUpdateAppearance} disabled={!isValid} >Update Appearance</Button>
                </Box>
            </Box>
        </Box>
    );
}